import Luiv3Constants from '@app/lui3/constants';
import styled, {css} from 'styled-components';

import {SvgIcon} from './Icon';
import InputContainer from './Input/InputContainer';
import {ColorName, colorVar} from './Theme/colors';
import Title from './Title';
import MaybeButton from './utils/MaybeButton';

interface Props {
  color?: ColorName;
  link?: boolean;
  center?: boolean;
  selected?: boolean;
  subHeading?: boolean;
  minWidth?: string;
  active?: boolean;
  thin?: boolean;
}

export const variants = {
  default: css`
    & > ${SvgIcon} {
      color: var(--theme-on-secondary-color);
    }
  `,
  link: css`
    font-weight: var(--theme-font-weight-link);
    color: var(--theme-color-link);
  `,
  selected: css`
    font-weight: var(--theme-font-weight-emphasize);
    color: var(--theme-color-onSurface);
  `,
  subHeading: css`
    font-weight: var(--theme-font-weight-emphasize);
    --badge-default-font-size: 0.75rem;
    --badge-default-padding: 0.5rem;
    font-size: 0.75rem;
    color: color-mix(
      in srgb,
      var(--theme-color-onSurface) calc(var(--theme-overline-opacity) * 100%),
      transparent
    );
  `,
} satisfies Record<string, ReturnType<typeof css>>;

const ListItem = styled(MaybeButton).attrs<Props>(({active}) => ({
  'data-active': active || undefined,
}))`
  line-height: 1.25;

  display: flex;
  align-items: center;
  gap: var(--theme-primitive-gap);
  text-align: left;

  padding: 0.25rem var(--list-item-padding, 1.25rem);

  min-height: 2rem;
  flex-shrink: 0;

  &:is(a, button, label):hover,
  &[data-active] {
    background-color: ${Luiv3Constants.baseColor['gray-50']};
    cursor: pointer;
  }

  ${Title} {
    margin-block: 0.25rem;
  }

  ${InputContainer} {
    width: unset;
    flex-grow: 1;
    flex-basis: 0;
  }

  &:disabled {
    filter: var(--theme-disabled-filter);
  }

  ${({minWidth}) => css`
    min-width: ${minWidth};
  `}

  ${({link, selected, subHeading}) =>
    variants[
      link
        ? 'link'
        : selected
          ? 'selected'
          : subHeading
            ? 'subHeading'
            : 'default'
    ]}

  ${({color}) =>
    color &&
    css`
      color: ${colorVar(color)};
    `}

  ${({center}) =>
    center &&
    css`
      justify-content: center;
    `}

  ${({thin}) =>
    thin &&
    css`
      padding-block: 0.125rem;
    `}
`;

export default ListItem;
